import type {
  WorkspaceBindingState,
  WorkspaceFilesystemState,
  WorkspaceProjection,
  WorkspaceWorkstationStatus,
} from '@localagent/sdk';

export function displayValue(value: string): string {
  return value.replace(/_/g, ' ');
}

export function workstationStatusLabel(status: WorkspaceWorkstationStatus): string {
  return status === 'starting' ? 'waking' : displayValue(status);
}

export function actionLabel(action: string): string {
  return displayValue(action).replace(/\b\w/g, (character) => character.toUpperCase());
}

/**
 * A typed workspace admission refusal, rendered as the state it is. Falls back
 * to the server's own sentence for refusals with no dedicated copy, so a new
 * reason still reads as itself rather than as a crash.
 */
export function workspaceRefusalMessage(refusal: string, serverMessage: string): string {
  switch (refusal) {
    case 'WORKSTATION_DISCONNECTED':
      return (
        `**This workspace lives on another computer.** ${serverMessage}\n\n` +
        'Start ShogunChop on that computer, or open the workspace and move it to this one.'
      );
    case 'FOLDER_MISSING':
      return (
        `**The bound folder is gone.** ${serverMessage}\n\n` +
        'Open the workspace to point it at the folder again.'
      );
    case 'WORKSPACE_DRAINING':
      return `**This workspace is busy.** ${serverMessage}`;
    case 'WORKSPACE_RETIRED':
      return `**This workspace is archived.** ${serverMessage}`;
    default:
      return `**Workspace turn refused:** ${serverMessage}`;
  }
}

export type ResourceTone = 'ok' | 'busy' | 'bad' | 'off';

export function workstationTone(status: WorkspaceWorkstationStatus): ResourceTone {
  switch (status) {
    case 'connected':
    case 'running':
      return 'ok';
    case 'pending':
    case 'starting':
    case 'sleeping':
    case 'reclaiming':
      return 'busy';
    case 'error':
    case 'failed':
      return 'bad';
    case 'disconnected':
    case 'retired':
      return 'off';
  }
}

export function filesystemTone(
  state: WorkspaceFilesystemState,
  bindingState?: WorkspaceBindingState
): ResourceTone {
  if (bindingState === 'missing') return 'bad';
  switch (state) {
    case 'ready':
      return 'ok';
    case 'provisioning':
      return 'busy';
    case 'error':
      return 'bad';
    case 'retired':
      return 'off';
  }
}

export function filesystemStatusLabel(projection: WorkspaceProjection): string {
  if (projection.binding?.state === 'missing') return 'missing';
  return displayValue(projection.filesystem.state);
}

export function workstationScreenCaption(
  status: WorkspaceWorkstationStatus,
  kind: WorkspaceProjection['workstation']['kind']
): string | null {
  switch (status) {
    case 'connected':
    case 'running':
    case 'starting':
      return null;
    case 'sleeping':
      return 'SLEEP';
    case 'reclaiming':
      return 'HALT';
    case 'pending':
      return 'WAIT';
    case 'error':
    case 'failed':
      return 'FAULT';
    case 'disconnected':
      return 'NO SIGNAL';
    case 'retired':
      return kind === 'cloud' ? 'OFF' : 'NO SIGNAL';
  }
}
